import { calculatePaymentSettlement } from "./payment-deposit/paymentDepositSettlementUtils";

export type PaymentDepositGroupBy =
  | "NONE"
  | "DATE"
  | "VENDOR"
  | "MANAGER"
  | "COMPANY"
  | "CARD";

export type PaymentDepositFilters = {
  dateFrom: string;
  dateTo: string;
  query: string;
  requestType: string;
  paymentCompany: string;
  createdBy: string;
  cardName: string;
  erpRegistered: string;
  groupBy: PaymentDepositGroupBy;
  sort: string;
};

const KOREA_OFFSET_MS = 9 * 60 * 60 * 1000;

const formatKoreaDateWithOffset = (date: Date, dayOffset = 0) => {
  const koreaDate = new Date(date.getTime() + KOREA_OFFSET_MS);
  koreaDate.setUTCDate(koreaDate.getUTCDate() + dayOffset);
  return `${koreaDate.getUTCFullYear()}-${String(
    koreaDate.getUTCMonth() + 1
  ).padStart(2, "0")}-${String(koreaDate.getUTCDate()).padStart(2, "0")}`;
};

export const getDefaultPaymentDepositFilters = (
  now = new Date()
): PaymentDepositFilters => {
  return {
    dateFrom: formatKoreaDateWithOffset(now, -14),
    dateTo: formatKoreaDateWithOffset(now, 14),
    query: "",
    requestType: "ALL",
    paymentCompany: "ALL",
    createdBy: "ALL",
    cardName: "ALL",
    erpRegistered: "ALL",
    groupBy: "NONE",
    sort: "NEWEST",
  };
};

export const getPaymentErpProcessingState = (request: any = {}) => {
  if (request.erpExclusion?.excluded === true) return "TEST_EXCLUDED";
  if (request.erpProcessingState) return String(request.erpProcessingState);
  if (request.erpBridge?.status === "VERIFIED") return "VERIFIED";
  if (request.depositManagement?.erpRegistered === true) {
    return "LEGACY_MANUAL_RECORDED";
  }
  return request.erpBridge?.status && request.erpBridge.status !== "PENDING"
    ? "PROCESSING"
    : "PENDING";
};

export const parsePaymentDepositTimestamp = (value = "") => {
  const timestampText = String(value || "").trim();
  const kcpTimestamp =
    /^(\d{4})(\d{2})(\d{2})(\d{2})?(\d{2})?(\d{2})?$/.exec(timestampText);
  if (kcpTimestamp) {
    const [, year, month, day, hour = "00", minute = "00", second = "00"] =
      kcpTimestamp;
    const calendarDate = new Date(
      Date.UTC(Number(year), Number(month) - 1, Number(day))
    );
    if (
      calendarDate.getUTCFullYear() !== Number(year) ||
      calendarDate.getUTCMonth() + 1 !== Number(month) ||
      calendarDate.getUTCDate() !== Number(day) ||
      Number(hour) > 23 ||
      Number(minute) > 59 ||
      Number(second) > 59 ||
      ![8, 12, 14].includes(timestampText.length)
    ) {
      return null;
    }
    const timestamp = Date.parse(
      `${year}-${month}-${day}T${hour}:${minute}:${second}+09:00`
    );
    return Number.isFinite(timestamp) ? timestamp : null;
  }
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(timestampText);
  if (isoDate) {
    const [, year, month, day] = isoDate;
    const calendarDate = new Date(
      Date.UTC(Number(year), Number(month) - 1, Number(day))
    );
    if (
      calendarDate.getUTCFullYear() !== Number(year) ||
      calendarDate.getUTCMonth() + 1 !== Number(month) ||
      calendarDate.getUTCDate() !== Number(day)
    ) {
      return null;
    }
  }
  const timestamp = Date.parse(timestampText);
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const getPaymentDepositDateTime = (request: any = {}) => {
  const source = String(
    request.approvedAt ||
      request.paidAt ||
      request.approval?.approvedAt ||
      ""
  ).trim();
  const timestamp = parsePaymentDepositTimestamp(source);
  if (timestamp === null) {
    return { date: "", time: "" };
  }
  const parts = new Intl.DateTimeFormat("ko-KR", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(timestamp));
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value || "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}:${get("second")}`,
  };
};

export const getPaymentDepositSourceLabel = (request: any = {}) => {
  const requestType = String(request.requestType || "").toUpperCase();
  if (requestType === "BUYLINK") return "문자링크";
  if (requestType === "KEYIN") return "키인결제";
  if (requestType === "AUTOPAY") return "등록카드";
  if (requestType === "STANDARD") return "표준결제";
  return requestType || "카드";
};

export const isApprovedPaymentForDepositLedger = (request: any = {}) => {
  const status = String(request.status || "").toUpperCase();
  const approvalStatus = String(request.approvalStatus || "").toUpperCase();
  const transactionId = String(request.transactionId || "").trim();
  const remainingAmount = Number(request.remainingAmount ?? request.amount) || 0;
  const cancelledAmount = Number(request.cancelledAmount) || 0;
  const fullyCancelled =
    status === "CANCELED" || (cancelledAmount > 0 && remainingAmount <= 0);
  const approved =
    status === "APPROVED" ||
    status === "PARTIALLY_CANCELED" ||
    approvalStatus === "APPROVED";
  return approved && Boolean(transactionId) && remainingAmount > 0 && !fullyCancelled;
};

const uniqueSorted = (values: unknown[]) =>
  Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean))
  ).sort((left, right) => left.localeCompare(right, "ko-KR"));

export const getPaymentDepositFilterOptions = (requests: any[] = []) => {
  const approved = requests.filter(isApprovedPaymentForDepositLedger);
  return {
    paymentCompanies: uniqueSorted(
      approved.map((request) => request.paymentCompanyName)
    ),
    createdBy: uniqueSorted(
      approved.map(
        (request) => request.requestedByName || request.createdBy || request.updatedBy
      )
    ),
    cardNames: uniqueSorted(
      approved.map((request) => request.approvalCardName)
    ),
  };
};

const getPaymentDepositSortTimestamp = (request: any = {}) =>
  parsePaymentDepositTimestamp(
    request.approvedAt || request.paidAt || request.approval?.approvedAt
  ) || 0;

export const filterApprovedPaymentDeposits = (
  requests: any[] = [],
  filters: Partial<PaymentDepositFilters> = {}
) => {
  const query = String(filters.query || "").trim().toLocaleLowerCase("ko-KR");
  const dateFrom = String(filters.dateFrom || "");
  const dateTo = String(filters.dateTo || "");
  const requestType = String(filters.requestType || "ALL").toUpperCase();
  const paymentCompany = String(filters.paymentCompany || "ALL");
  const createdBy = String(filters.createdBy || "ALL");
  const cardName = String(filters.cardName || "ALL");
  const erpRegistered = String(filters.erpRegistered || "ALL").toUpperCase();
  const sort = String(filters.sort || "NEWEST").toUpperCase();

  return requests
    .filter(isApprovedPaymentForDepositLedger)
    .filter((request) => {
      const { date } = getPaymentDepositDateTime(request);
      if ((dateFrom || dateTo) && !date) return false;
      if (dateFrom && date && date < dateFrom) return false;
      if (dateTo && date && date > dateTo) return false;
      if (requestType !== "ALL" && request.requestType !== requestType) return false;
      if (
        paymentCompany !== "ALL" &&
        String(request.paymentCompanyName || "") !== paymentCompany
      ) {
        return false;
      }
      const requestManager =
        request.requestedByName || request.createdBy || request.updatedBy || "";
      if (createdBy !== "ALL" && requestManager !== createdBy) return false;
      if (
        cardName !== "ALL" &&
        String(request.approvalCardName || "") !== cardName
      ) {
        return false;
      }
      const erpProcessingState = getPaymentErpProcessingState(request);
      if (erpRegistered !== "ALL" && erpRegistered !== erpProcessingState) {
        return false;
      }
      if (query) {
        const searchable = [
          request.vendorCode,
          request.vendorBusinessName,
          request.vendorBusinessNumber,
          request.customerName,
          request.requestedByName,
          request.createdBy,
          request.paymentCompanyName,
          request.paymentSiteCd,
          request.paymentSiteCdMasked,
          request.approvalCode,
          request.transactionId,
          request.approvalCardName,
          getPaymentDepositSourceLabel(request),
          request.depositManagement?.note,
        ]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("ko-KR");
        const compactQuery = query.replace(/[^0-9a-z가-힣]/gi, "");
        const compactSearchable = searchable.replace(/[^0-9a-z가-힣]/gi, "");
        if (
          !searchable.includes(query) &&
          (!compactQuery || !compactSearchable.includes(compactQuery))
        ) {
          return false;
        }
      }
      return true;
    })
    .sort((left, right) => {
      if (sort === "OLDEST") {
        return (
          getPaymentDepositSortTimestamp(left) -
          getPaymentDepositSortTimestamp(right)
        );
      }
      if (sort === "AMOUNT_DESC") {
        return (
          (Number(right.remainingAmount ?? right.amount) || 0) -
          (Number(left.remainingAmount ?? left.amount) || 0)
        );
      }
      if (sort === "AMOUNT_ASC") {
        return (
          (Number(left.remainingAmount ?? left.amount) || 0) -
          (Number(right.remainingAmount ?? right.amount) || 0)
        );
      }
      return (
        getPaymentDepositSortTimestamp(right) -
        getPaymentDepositSortTimestamp(left)
      );
    });
};

const getPaymentDepositGroupKey = (
  request: any,
  groupBy: PaymentDepositGroupBy
) => {
  if (groupBy === "DATE") return getPaymentDepositDateTime(request).date || "날짜 미확인";
  if (groupBy === "VENDOR") {
    return `${request.vendorCode || "코드 미연결"} · ${
      request.vendorBusinessName || request.customerName || "거래처 미연결"
    }`;
  }
  if (groupBy === "MANAGER") {
    return request.requestedByName || request.createdBy || "담당자 미확인";
  }
  if (groupBy === "COMPANY") {
    return request.paymentCompanyName || "거래사업자 미확인";
  }
  if (groupBy === "CARD") return request.approvalCardName || "카드종류 미확인";
  return "";
};

export const groupPaymentDeposits = (
  requests: any[] = [],
  groupBy: PaymentDepositGroupBy = "NONE"
) => {
  if (groupBy === "NONE") {
    return [
      {
        key: "ALL",
        label: "",
        items: requests,
        totalAmount: requests.reduce(
          (sum, request) =>
            sum + (Number(request.remainingAmount ?? request.amount) || 0),
          0
        ),
      },
    ];
  }
  const groups = new Map<string, any[]>();
  requests.forEach((request) => {
    const key = getPaymentDepositGroupKey(request, groupBy);
    groups.set(key, [...(groups.get(key) || []), request]);
  });
  return Array.from(groups.entries()).map(([key, items]) => ({
    key,
    label: key,
    items,
    totalAmount: items.reduce(
      (sum, request) =>
        sum + (Number(request.remainingAmount ?? request.amount) || 0),
      0
    ),
  }));
};

export const buildPaymentDepositExportRows = (requests: any[] = []) =>
  requests.filter(isApprovedPaymentForDepositLedger).map((request) => {
    const approved = getPaymentDepositDateTime(request);
    const sourceLabel = getPaymentDepositSourceLabel(request);
    const depositManagement = request.depositManagement || {};
    const manager =
      request.requestedByName || request.createdBy || request.updatedBy || "";
    const depositAmount = Number(request.remainingAmount ?? request.amount) || 0;
    const settlement = calculatePaymentSettlement(request, approved.date);
    const feeVatDisplay =
      settlement.policy.feeVatMode === "INCLUDED"
        ? "수수료에 포함"
        : settlement.policy.feeVatMode === "EXCLUDED" &&
          settlement.feeVatAmount !== null
        ? settlement.feeVatAmount
        : "확인 필요";
    return {
      거래일자: approved.date,
      거래시간: approved.time,
      지급예정일: settlement.expectedDepositDate,
      입금예정일: settlement.expectedDepositDate,
      실제입금일: depositManagement.depositDate || "",
      입금일자: depositManagement.depositDate || "",
      결제방법: "카드",
      거래처코드: request.vendorCode || "",
      거래처명: request.vendorBusinessName || request.customerName || "",
      담당자: manager,
      입금액: depositAmount,
      승인번호: request.approvalCode || "",
      거래사업자: request.paymentCompanyName || "",
      결제경로: sourceLabel,
      승인번호_거래사업자_결제경로: [
        request.approvalCode,
        request.paymentCompanyName,
        sourceLabel,
      ]
        .filter(Boolean)
        .join("_"),
      카드종류: request.approvalCardName || "",
      입금등록완료: depositManagement.erpRegistered === true ? "완료" : "미등록",
      비고: depositManagement.note || "",
      KCP사이트코드: request.paymentSiteCd || request.paymentSiteCdMasked || "",
      거래번호: request.transactionId || "",
      승인금액: Number(request.amount) || 0,
      취소금액: Number(request.cancelledAmount) || 0,
      승인잔액: settlement.settlementAmount,
      수수료율:
        settlement.policy.feeRateBasisPoints === null
          ? "정책 미등록"
          : `${(settlement.policy.feeRateBasisPoints / 100).toFixed(2)}%`,
      수수료금액: settlement.feeAmount ?? "",
      수수료VAT방식: settlement.policy.feeVatMode,
      수수료부가세: feeVatDisplay,
      수수료부가세금액:
        settlement.policy.feeVatMode === "EXCLUDED"
          ? settlement.feeVatAmount ?? ""
          : "",
      총차감액: settlement.totalDeductionAmount ?? "",
      예상입금액: settlement.expectedDepositAmount ?? "",
      정산정책출처: settlement.policy.source,
      결제요청ID: request.paymentRequestId || request.id || "",
      ERP등록일시: depositManagement.erpRegisteredAt || "",
      ERP등록담당자: depositManagement.erpRegisteredBy || "",
    };
  });
